import { useEffect, useState } from 'react';
import './ProgressRing.css';

/**
 * ProgressRing - Cerchio di progresso animato per percentuali
 *
 * @param {number} value - Valore percentuale (0-100)
 * @param {number} size - Dimensione in pixel (default: 48)
 * @param {number} strokeWidth - Spessore del cerchio (default: 4)
 * @param {string} color - Colore del progresso (default: auto basato su soglie)
 * @param {boolean} showValue - Mostrare il valore al centro (default: true)
 * @param {string} label - Label sotto il ring
 * @param {object} thresholds - Soglie per colori semaforo { warning: 10, danger: 20 }
 * @param {boolean} inverse - Se true, valori bassi sono buoni (es. assenteismo)
 */
const ProgressRing = ({
    value = 0,
    size = 48,
    strokeWidth = 4,
    color = null,
    showValue = true,
    label = '',
    thresholds = { warning: 10, danger: 20 },
    inverse = false
}) => {
    const [animatedValue, setAnimatedValue] = useState(0);

    // Calcola le dimensioni del cerchio
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const normalizedValue = Math.min(Math.max(value, 0), 100);
    const strokeDashoffset = circumference - (animatedValue / 100) * circumference;

    // Determina il colore basato sulle soglie
    const getColor = () => {
        if (color) return color;

        if (inverse) {
            // Per valori inversi (es. assenteismo): basso = buono (verde), alto = cattivo (rosso)
            if (value < thresholds.warning) return 'var(--oper-hr-color-success, #059669)';
            if (value < thresholds.danger) return 'var(--oper-hr-color-warning, #d97706)';
            return 'var(--oper-hr-color-danger, #dc2626)';
        } else {
            // Per valori normali: alto = buono (verde), basso = cattivo (rosso)
            if (value >= 100 - thresholds.warning) return 'var(--oper-hr-color-success, #059669)';
            if (value >= 100 - thresholds.danger) return 'var(--oper-hr-color-warning, #d97706)';
            return 'var(--oper-hr-color-danger, #dc2626)';
        }
    };

    // Animazione del valore
    useEffect(() => {
        const duration = 800;
        const startTime = Date.now();
        const startValue = animatedValue;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Easing ease-out
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const current = startValue + (normalizedValue - startValue) * easeOut;

            setAnimatedValue(current);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }, [normalizedValue]);

    const progressColor = getColor();

    // Calcola l'altezza totale includendo la label
    const totalHeight = label ? size + 18 : size;

    return (
        <div className="progress-ring-container" style={{ width: size, minHeight: totalHeight }}>
            <div style={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} className="progress-ring-svg">
                    {/* Cerchio di sfondo */}
                    <circle
                        className="progress-ring-background"
                        stroke="#cbd5e1"
                        fill="transparent"
                        strokeWidth={strokeWidth}
                        r={radius}
                        cx={size / 2}
                        cy={size / 2}
                    />
                    {/* Cerchio di progresso */}
                    <circle
                        className="progress-ring-progress"
                        stroke={progressColor}
                        fill="transparent"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        r={radius}
                        cx={size / 2}
                        cy={size / 2}
                        style={{
                            transform: 'rotate(-90deg)',
                            transformOrigin: '50% 50%',
                            transition: 'stroke 0.3s ease'
                        }}
                    />
                </svg>
                {showValue && (
                    <div className="progress-ring-value" style={{ color: progressColor }}>
                        {Math.round(animatedValue)}%
                    </div>
                )}
            </div>
            {label && <div className="progress-ring-label">{label}</div>}
        </div>
    );
};

export default ProgressRing;
